import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldWarning, FileText, ClockClockwise, Lock, Database,
  Users, Eye, DownloadSimple, SignIn, Warning, Archive, Export, CalendarBlank,
  Siren, BellRinging, CheckCircle as CheckCircleIcon, PaperPlaneRight,
  UserMinus, UserPlus, UserCircle, Stamp, FilePdf,
} from "@phosphor-icons/react";

export default function HipaaCompliance() {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [filters, setFilters] = useState({ action: "", resource_type: "", user_email: "" });
  const [retention, setRetention] = useState(null);
  const [hotDays, setHotDays] = useState(90);
  const [totalYears, setTotalYears] = useState(6);
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [secCfg, setSecCfg] = useState(null);
  const [bhStart, setBhStart] = useState(6);
  const [bhEnd, setBhEnd] = useState(21);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [officerEmail, setOfficerEmail] = useState("");
  const [generating, setGenerating] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  const loadAll = async () => {
    try {
      const [s, sm, al, ret, alerts_, sc, us, rep, off] = await Promise.all([
        api.get("/hipaa/status"),
        api.get("/audit-log/summary?days=30"),
        api.get("/audit-log?limit=100"),
        api.get("/hipaa/retention"),
        api.get("/hipaa/security-alerts?limit=50"),
        api.get("/hipaa/security-config"),
        api.get("/admin/users"),
        api.get("/hipaa/compliance-reports"),
        api.get("/hipaa/compliance-officer"),
      ]);
      setStatus(s.data);
      setSummary(sm.data);
      setAuditLog(al.data);
      setRetention(ret.data);
      setHotDays(ret.data.retention.hot_days);
      setTotalYears(ret.data.retention.total_years);
      setAlerts(alerts_.data);
      setSecCfg(sc.data);
      setBhStart(sc.data.business_hours_start);
      setBhEnd(sc.data.business_hours_end);
      setUsers(us.data);
      setReports(rep.data);
      setOfficerEmail(off.data.email || "");
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("HIPAA compliance view requires admin or auditor role");
      } else {
        toast.error("Failed to load HIPAA status");
      }
    }
  };

  useEffect(() => { loadAll(); }, []);

  const reloadLog = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([k, v]) => v));
      params.limit = 200;
      const r = await api.get("/audit-log", { params });
      setAuditLog(r.data);
    } catch { toast.error("Failed"); }
  };

  const toggleBaa = async (vendor, key, signed) => {
    try {
      const next = {};
      (status?.administrative_safeguards?.baa_required || []).forEach((b) => {
        next[baaKey(b.vendor)] = b.signed;
      });
      next[key] = signed;
      await api.put("/hipaa/baa-status", next);
      toast.success("BAA status updated");
      loadAll();
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Admins only");
      else toast.error("Update failed");
    }
  };

  const saveRetention = async () => {
    try {
      await api.put("/hipaa/retention", { hot_days: Number(hotDays), total_years: Number(totalYears) });
      toast.success("Retention policy saved");
      loadAll();
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Admins only");
      else toast.error("Save failed");
    }
  };

  const runArchive = async () => {
    setArchiving(true);
    try {
      const r = await api.post("/hipaa/archive/run");
      const n = r.data.archived_entries || 0;
      toast.success(n > 0 ? `Archived ${n} entries to encrypted storage` : "Nothing to archive (no entries past retention)");
      loadAll();
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Admins only");
      else toast.error("Archive failed");
    } finally { setArchiving(false); }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (exportFrom) params.date_from = exportFrom;
      if (exportTo) params.date_to = exportTo;
      const r = await api.get("/audit-log/export", { params, responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_export_${exportFrom || "all"}_${exportTo || "now"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const downloadArchive = async (archiveId, label) => {
    try {
      const r = await api.get(`/hipaa/archive/${archiveId}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/gzip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}.jsonl.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Archive downloaded");
    } catch { toast.error("Download failed"); }
  };

  const saveSecConfig = async () => {
    try {
      const r = await api.put("/hipaa/security-config", {
        security_alerts_enabled: !!secCfg?.security_alerts_enabled,
        business_hours_start: Number(bhStart),
        business_hours_end: Number(bhEnd),
      });
      setSecCfg(r.data);
      toast.success("Security config saved");
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Admins only");
      else toast.error("Save failed");
    }
  };

  const fireTestAlert = async () => {
    try {
      const r = await api.post("/hipaa/security-alerts/test");
      const sent = (r.data.dispatch || []).filter((x) => x.status === "sent");
      toast.success(`Test alert fired · ${sent.length} channel(s) sent`);
      loadAll();
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Admins only");
      else toast.error("Test failed");
    }
  };

  const acknowledgeAlert = async (id) => {
    try {
      await api.post(`/hipaa/security-alerts/${id}/acknowledge`);
      toast.success("Alert acknowledged");
      loadAll();
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Admins only");
      else toast.error("Acknowledge failed");
    }
  };

  const suspendUser = async (user) => {
    const reason = window.prompt(`Suspend ${user.email}? Enter reason:`, "Suspicious activity");
    if (!reason) return;
    try {
      await api.post(`/admin/users/${user.id}/suspend`, { reason });
      toast.success(`${user.email} suspended`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suspend failed");
    }
  };

  const reactivateUser = async (user) => {
    if (!window.confirm(`Reactivate ${user.email}?`)) return;
    try {
      await api.post(`/admin/users/${user.id}/reactivate`);
      toast.success(`${user.email} reactivated`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reactivate failed");
    }
  };

  const changeRole = async (user, newRole) => {
    if (user.role === newRole) return;
    try {
      await api.post(`/admin/users/${user.id}/role`, { role: newRole });
      toast.success(`Role changed to ${newRole}`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Role change failed");
    }
  };

  const [resetResult, setResetResult] = useState(null);
  const adminResetPassword = async (user) => {
    if (!window.confirm(
      `Reset password for ${user.email}?\n\n` +
      `A new temporary password will be generated, hashed into the database, and ` +
      `texted via Twilio to the cell phone on file. The existing password is overwritten, ` +
      `never read. You'll see the new temp password ONCE here in case SMS fails.`
    )) return;
    try {
      const r = await api.post(`/admin/users/${user.id}/reset-password`);
      setResetResult(r.data);
      const ok = r.data.sms_status?.status === "sent";
      if (ok) {
        toast.success(`New password texted to ${r.data.phone}`);
      } else {
        toast.warning("Password reset — SMS not delivered (see fallback)");
      }
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const saveOfficer = async () => {
    try {
      await api.put("/hipaa/compliance-officer", { email: officerEmail });
      toast.success("Compliance officer email saved");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const generateAttestation = async () => {
    try {
      const r = await api.get("/hipaa/compliance-report", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hipaa_attestation_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Attestation PDF downloaded");
    } catch { toast.error("Generation failed"); }
  };

  const runAttestationJob = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/hipaa/compliance-reports/run");
      const dispatch = r.data?.latest?.email_dispatch || [];
      const sent = dispatch.filter((d) => d.status === "sent").length;
      const skipped = dispatch.filter((d) => d.status !== "sent").length;
      if (sent > 0) toast.success(`Attestation archived. Emailed to ${sent} recipient(s).`);
      else if (skipped > 0) toast.warning(`Attestation archived. Email skipped: ${dispatch[0].reason || dispatch[0].error}`);
      else toast.success("Attestation archived (no email recipient configured)");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Job failed");
    } finally { setGenerating(false); }
  };

  const downloadStoredReport = async (id, periodStart, periodEnd) => {
    try {
      const r = await api.get(`/hipaa/compliance-reports/${id}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hipaa_attestation_${periodStart}_to_${periodEnd}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Download failed"); }
  };


  if (!status) return <div className="text-gray-500">Loading HIPAA compliance…</div>;

  const tech = status.technical_safeguards;
  const baas = status.administrative_safeguards.baa_required;
  const baaPolicies = status.administrative_safeguards.policies_required;
  const baaSignedCount = baas.filter((b) => b.signed).length;

  return (
    <div className="space-y-8" data-testid="hipaa-page">
      <header className="border-b border-gray-200 pb-5">
        <div className="overline text-gray-500">Security &amp; Privacy</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">HIPAA Compliance</h1>
        <p className="text-sm text-gray-600 mt-2 max-w-3xl">
          Technical safeguards built into the app, administrative responsibilities your organization must complete,
          and a live audit log of every PHI access and mutation.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-5 border border-gray-200 bg-white">
        <Kpi label="Technical safeguards" value="9 / 9" sub="enabled by app" color="#16A34A" icon={ShieldCheck} testid="kpi-technical" />
        <Kpi label="BAAs signed" value={`${baaSignedCount} / ${baas.length}`} sub="vendor responsibility" color={baaSignedCount === baas.length ? "#16A34A" : "#D97706"} icon={FileText} testid="kpi-baas" />
        <Kpi label="Audit events (30d)" value={summary?.actions?.reduce((a, b) => a + b.count, 0) || 0} sub={`${summary?.phi_views || 0} PHI views`} icon={ClockClockwise} testid="kpi-events" />
        <Kpi label="Failed logins (30d)" value={summary?.failed_logins || 0} sub={`${summary?.lockouts || 0} lockouts`} color={(summary?.failed_logins || 0) > 5 ? "#D97706" : "#0F172A"} icon={Warning} testid="kpi-failed" />
        <Kpi
          label="Open security alerts"
          value={alerts.filter((a) => !a.acknowledged).length}
          sub={`${alerts.length} total`}
          color={alerts.some((a) => !a.acknowledged && a.severity === "HIGH") ? "#DC2626" : alerts.some((a) => !a.acknowledged) ? "#D97706" : "#16A34A"}
          icon={Siren}
          testid="kpi-alerts"
        />
      </div>

      <section className="bg-white border border-gray-200" data-testid="security-alerts-section">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="overline text-gray-500">Real-time intrusion detection</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Break-glass security alerts</h2>
            <p className="text-xs text-gray-600 mt-2 max-w-2xl">
              Watches the audit stream for bulk deletes, credential stuffing, mass PHI views, off-hours mutations,
              account lockouts, and audit-log burst access. Fires SMS + email to coordinators in real time.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-sm border border-gray-200 px-3 py-2 bg-gray-50">
              <input
                type="checkbox"
                checked={!!secCfg?.security_alerts_enabled}
                onChange={(e) => setSecCfg({ ...secCfg, security_alerts_enabled: e.target.checked })}
                data-testid="sec-enabled-toggle"
              />
              <span>Engine enabled</span>
            </label>
            <div className="flex items-center gap-1 text-xs">
              <span className="text-gray-500 uppercase tracking-wider font-semibold">Business hrs (CT)</span>
              <input type="number" min="0" max="23" value={bhStart} onChange={(e) => setBhStart(e.target.value)} className="w-14 border border-gray-300 bg-white px-2 py-1 text-xs font-mono" data-testid="bh-start" />
              <span className="text-gray-500">–</span>
              <input type="number" min="0" max="24" value={bhEnd} onChange={(e) => setBhEnd(e.target.value)} className="w-14 border border-gray-300 bg-white px-2 py-1 text-xs font-mono" data-testid="bh-end" />
            </div>
            <button onClick={saveSecConfig} className="border border-gray-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100" data-testid="sec-save-btn">Save</button>
            <button onClick={fireTestAlert} className="bg-slate-900 text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 inline-flex items-center gap-2" data-testid="fire-test-alert-btn">
              <PaperPlaneRight size={12} weight="bold" /> Fire test alert
            </button>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="security-alerts-table">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
                <th className="px-6 py-2 font-semibold">When</th>
                <th className="px-3 py-2 font-semibold">Severity</th>
                <th className="px-3 py-2 font-semibold">Rule</th>
                <th className="px-3 py-2 font-semibold">Message</th>
                <th className="px-3 py-2 font-semibold">Dispatched</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold text-right"></th>
              </tr>
            </thead>
            <tbody>
              {alerts.length === 0 && (
                <tr><td colSpan="7" className="p-10 text-center text-gray-500">
                  <BellRinging size={28} weight="duotone" className="mx-auto text-slate-300 mb-2" />
                  No alerts. Engine is watching — try "Fire test alert" to verify the dispatch path.
                </td></tr>
              )}
              {alerts.map((a) => {
                const sent = (a.dispatch || []).filter((d) => d.status === "sent").length;
                const skipped = (a.dispatch || []).filter((d) => d.status !== "sent").length;
                return (
                  <tr key={a.id} className={`data-row border-b border-gray-100 ${a.acknowledged ? "opacity-60" : ""}`}>
                    <td className="px-6 py-2 font-mono text-slate-700">{a.created_at.replace("T", " ").slice(0, 19)}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 font-bold ${a.severity === "HIGH" ? "bg-red-100 text-red-800" : a.severity === "MEDIUM" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>{a.severity}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-900">
                      {a.rule}
                      {a.auto_suspended && <div className="text-[10px] text-red-700 mt-0.5">↳ auto-suspended {a.auto_suspended.user_email}</div>}
                    </td>
                    <td className="px-3 py-2 text-gray-700 max-w-md">{a.message}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">
                      <span className="text-emerald-700">{sent} sent</span>
                      {skipped > 0 && <span className="text-gray-500"> · {skipped} skipped</span>}
                    </td>
                    <td className="px-3 py-2">
                      {a.acknowledged ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircleIcon size={12} weight="bold" /> ack'd by {a.acknowledged_by}</span>
                      ) : (
                        <span className="text-amber-700 font-semibold">OPEN</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!a.acknowledged && (
                        <button
                          onClick={() => acknowledgeAlert(a.id)}
                          className="text-slate-700 hover:text-slate-900 text-[10px] uppercase tracking-wider font-semibold"
                          data-testid={`ack-${a.id}`}
                        >
                          Acknowledge
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="overline text-gray-500">45 CFR §164.312</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Technical safeguards</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
              <th className="px-6 py-3 font-semibold">Safeguard</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Implementation</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(tech).map(([k, v]) => (
              <tr key={k} className="data-row border-b border-gray-100">
                <td className="px-6 py-3 font-medium text-slate-900">{prettyKey(k)}</td>
                <td className="px-4 py-3">
                  <span className={`status-pill ${v.status === "ENABLED" ? "status-current" : v.status === "DEPENDS" ? "status-exp-30" : "status-missing"}`}>
                    {v.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700 text-xs">{v.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="bg-white border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="overline text-gray-500">Your responsibility (not handled by the app alone)</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Business Associate Agreements</h2>
          <p className="text-xs text-gray-600 mt-2 max-w-2xl">
            HIPAA requires a signed BAA with every vendor that processes PHI on your behalf. Check each box once your
            BAA is signed and on file. This is an administrative attestation only — the app does not verify with the vendor.
          </p>
        </div>
        <table className="w-full text-sm" data-testid="baa-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
              <th className="px-6 py-3 font-semibold">Vendor</th>
              <th className="px-4 py-3 font-semibold">Why required</th>
              <th className="px-4 py-3 font-semibold">BAA Link</th>
              <th className="px-4 py-3 font-semibold">Signed</th>
            </tr>
          </thead>
          <tbody>
            {baas.map((b) => {
              const key = baaKey(b.vendor);
              return (
                <tr key={b.vendor} className="data-row border-b border-gray-100">
                  <td className="px-6 py-3 font-medium text-slate-900">{b.vendor}</td>
                  <td className="px-4 py-3 text-gray-700 text-xs max-w-md">{b.note}</td>
                  <td className="px-4 py-3 text-xs">
                    {b.url ? <a href={b.url} target="_blank" rel="noreferrer" className="text-slate-700 hover:text-slate-900 underline">Vendor BAA →</a> : <span className="text-gray-500">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={!!b.signed} onChange={(e) => toggleBaa(b.vendor, key, e.target.checked)} data-testid={`baa-${key}`} />
                      <span className="font-mono">{b.signed ? "ATTESTED" : "MISSING"}</span>
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="bg-white border border-gray-200 p-6">
        <div className="overline text-gray-500">Administrative safeguards</div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5 mb-3">Required policies &amp; procedures</h2>
        <p className="text-xs text-gray-600 mb-4 max-w-2xl">{status.patient_rights_notice}</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
          {baaPolicies.map((p) => (
            <li key={p} className="flex items-start gap-2 border-l-2 border-amber-400 pl-3 py-1">
              <ShieldWarning size={16} weight="bold" className="text-amber-600 mt-0.5 flex-shrink-0" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-white border border-gray-200" data-testid="attestation-section">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="overline text-gray-500">§164.308(a)(8) · Periodic evaluation</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Compliance attestation reports</h2>
          <p className="text-xs text-gray-600 mt-2 max-w-3xl">
            A monthly attestation PDF is auto-generated on the 1st of each month at 03:00 CT, archived to encrypted
            object storage, and emailed to your designated Privacy / Security Officer. It satisfies the HIPAA Security Rule's
            periodic-evaluation requirement and gives leadership a single one-line monthly status email instead of
            "did anyone check the dashboard this month?"
          </p>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end bg-gray-50">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Privacy / Security Officer email</label>
            <input
              type="email"
              value={officerEmail}
              onChange={(e) => setOfficerEmail(e.target.value)}
              placeholder="officer@yourorg.org"
              className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm font-mono"
              data-testid="officer-email-input"
            />
          </div>
          <button onClick={saveOfficer} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-white hover:border-slate-900" data-testid="save-officer-btn">
            Save officer
          </button>
          <button onClick={generateAttestation} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-white hover:border-slate-900 inline-flex items-center gap-2" data-testid="generate-pdf-btn">
            <FilePdf size={14} weight="bold" /> Preview PDF
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center gap-3">
          <button onClick={runAttestationJob} disabled={generating} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 inline-flex items-center gap-2 disabled:opacity-60" data-testid="run-attestation-btn">
            <Stamp size={14} weight="bold" /> {generating ? "Generating…" : "Run monthly attestation now"}
          </button>
          <div className="text-xs text-gray-500">Same flow as the 1st-of-month cron: archive + email officer.</div>
        </div>

        {reports.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="reports-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
                  <th className="px-6 py-2 font-semibold">Period</th>
                  <th className="px-3 py-2 font-semibold">Events</th>
                  <th className="px-3 py-2 font-semibold">Alerts (H/M/L)</th>
                  <th className="px-3 py-2 font-semibold">BAAs</th>
                  <th className="px-3 py-2 font-semibold">Generated</th>
                  <th className="px-3 py-2 font-semibold">Email</th>
                  <th className="px-3 py-2 font-semibold text-right"></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => {
                  const ss = r.stats_summary || {};
                  const sev = ss.alerts_by_severity || {};
                  const sent = (r.email_dispatch || []).filter((d) => d.status === "sent").length;
                  return (
                    <tr key={r.id} className="data-row border-b border-gray-100 font-mono">
                      <td className="px-6 py-2 text-slate-700">{r.period_start} → {r.period_end}</td>
                      <td className="px-3 py-2">{(ss.total_events ?? 0).toLocaleString()}</td>
                      <td className="px-3 py-2">
                        <span className="text-red-700 font-bold">{sev.HIGH ?? 0}</span>
                        <span className="text-gray-400 mx-1">·</span>
                        <span className="text-amber-700">{sev.MEDIUM ?? 0}</span>
                        <span className="text-gray-400 mx-1">·</span>
                        <span className="text-sky-700">{sev.LOW ?? 0}</span>
                      </td>
                      <td className="px-3 py-2">{ss.baa_signed || "—"}</td>
                      <td className="px-3 py-2 text-gray-500">{r.generated_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="px-3 py-2 text-gray-500">{sent > 0 ? `sent (${sent})` : "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => downloadStoredReport(r.id, r.period_start, r.period_end)}
                          className="text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold"
                          data-testid={`dl-report-${r.id}`}
                        >
                          <DownloadSimple size={12} weight="bold" /> PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200" data-testid="user-mgmt-section">
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
          <div>
            <div className="overline text-gray-500">§164.308(a)(3) · Workforce security</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">User accounts</h2>
            <p className="text-xs text-gray-600 mt-2 max-w-3xl">
              HIGH-severity break-glass alerts (bulk delete, mass PHI view) auto-suspend non-admin accounts.
              Suspended users cannot log in or make API calls until an admin reactivates them here.
              Admin accounts are never auto-suspended (to prevent lockout).
            </p>
            <div className="mt-3 border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-900 max-w-3xl">
              <strong>§164.308(a)(5)(ii)(D) · Password management:</strong> Passwords are stored as bcrypt hashes
              and cannot be retrieved by anyone, including administrators. To help a user, use <span className="font-semibold">Reset PW</span> —
              a new temp password is generated, hashed in, and texted via Twilio to the cell phone on file.
              Only admins can promote users (including to admin); coordinators cannot.
            </div>
          </div>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <button
              onClick={async () => {
                if (!window.confirm(
                  "Wipe ALL demo / sample data?\n\n" +
                  "This permanently deletes:\n" +
                  "  • Every practitioner\n" +
                  "  • Every credential\n" +
                  "  • Every uploaded document\n" +
                  "  • Every committee decision\n" +
                  "  • Every reminder log entry\n" +
                  "  • The 'admin@ascdemo.org' demo account\n\n" +
                  "KEEPS your user accounts, the audit log (6-yr HIPAA retention), and security alerts.\n\n" +
                  "This cannot be undone. Continue?"
                )) return;
                try {
                  const r = await api.post("/admin/wipe-demo-data");
                  const d = r.data.deleted;
                  toast.success(
                    `Wiped: ${d.practitioners} practitioners, ${d.credentials} credentials, ` +
                    `${d.documents} documents, ${d.reminders} reminders, ` +
                    `${d.demo_admin_user} demo admin`
                  );
                  loadAll();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Wipe failed");
                }
              }}
              className="border border-red-300 text-red-700 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-red-50 inline-flex items-center gap-2"
              data-testid="wipe-demo-data-btn"
              title="Permanently delete all practitioners, credentials, documents, and the demo admin"
            >
              Wipe demo data
            </button>
            <button
              onClick={() => setShowInvite(true)}
              className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 inline-flex items-center gap-2"
              data-testid="invite-user-btn"
            >
              <UserPlus size={14} weight="bold" /> Invite user
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="users-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
                <th className="px-6 py-2 font-semibold">Email</th>
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Role</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Suspended detail</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((usr) => (
                <tr key={usr.id} className="data-row border-b border-gray-100">
                  <td className="px-6 py-2 font-medium text-slate-900">{usr.email}</td>
                  <td className="px-3 py-2 text-gray-700">{usr.name}</td>
                  <td className="px-3 py-2">
                    <select
                      value={usr.role}
                      onChange={(e) => changeRole(usr, e.target.value)}
                      className="border border-gray-300 bg-white px-2 py-1 text-[11px] font-mono"
                      data-testid={`role-${usr.id}`}
                    >
                      <option value="admin">admin</option>
                      <option value="coordinator">coordinator</option>
                      <option value="auditor">auditor</option>
                      <option value="practitioner">practitioner</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    {usr.suspended ? (
                      <span className="status-pill status-expired">SUSPENDED</span>
                    ) : (
                      <span className="status-pill status-current">ACTIVE</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600 max-w-md">
                    {usr.suspended ? (
                      <div className="text-[11px]">
                        <div className="text-red-700 font-mono">{usr.suspended_at?.slice(0, 19)}</div>
                        <div>{usr.suspended_reason}</div>
                        <div className="text-gray-500">by {usr.suspended_by}</div>
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      <button
                        onClick={() => adminResetPassword(usr)}
                        className="inline-flex items-center gap-1 border border-slate-300 text-slate-700 px-3 py-1 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-100"
                        data-testid={`reset-pw-${usr.id}`}
                        title="Generate a new temp password and text it via Twilio"
                      >
                        Reset PW
                      </button>
                      {usr.suspended ? (
                        <button
                          onClick={() => reactivateUser(usr)}
                          className="inline-flex items-center gap-1 bg-emerald-600 text-white px-3 py-1 text-[10px] uppercase tracking-wider font-semibold hover:bg-emerald-700"
                          data-testid={`reactivate-${usr.id}`}
                        >
                          <UserPlus size={12} weight="bold" /> Reactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => suspendUser(usr)}
                          className="inline-flex items-center gap-1 border border-red-300 text-red-700 px-3 py-1 text-[10px] uppercase tracking-wider font-semibold hover:bg-red-50"
                          data-testid={`suspend-${usr.id}`}
                        >
                          <UserMinus size={12} weight="bold" /> Suspend
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white border border-gray-200" data-testid="retention-section">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="overline text-gray-500">§164.316(b)(2)(i) · 6-year retention</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Audit retention &amp; export</h2>
          <p className="text-xs text-gray-600 mt-2 max-w-3xl">
            Hot audit events live in MongoDB for fast queries. Older entries are automatically archived monthly to
            encrypted object storage as compressed JSONL — keeping the live table snappy while preserving HIPAA's
            6-year retention. Use the export tool to assemble OCR / Office for Civil Rights audit packets.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 border-b border-gray-200">
          <div className="p-5 border-r border-gray-200">
            <div className="overline text-gray-500">Hot table</div>
            <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{retention?.hot_count ?? "—"}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">events in MongoDB</div>
            {retention?.hot_oldest && (
              <div className="text-[11px] text-gray-500 mt-2">Oldest: <span className="font-mono">{retention.hot_oldest.slice(0, 10)}</span></div>
            )}
          </div>
          <div className="p-5 border-r border-gray-200">
            <div className="overline text-gray-500">Cold archives</div>
            <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{retention?.archives?.length ?? 0}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">encrypted JSONL files</div>
          </div>
          <div className="p-5 grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-500">Hot days</label>
              <input type="number" min="7" value={hotDays} onChange={(e) => setHotDays(e.target.value)} className="mt-1 w-full border border-gray-300 bg-white px-2 py-1.5 text-sm font-mono" data-testid="retention-hot-days" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-500">Total yrs</label>
              <input type="number" min="1" max="20" value={totalYears} onChange={(e) => setTotalYears(e.target.value)} className="mt-1 w-full border border-gray-300 bg-white px-2 py-1.5 text-sm font-mono" data-testid="retention-total-years" />
            </div>
            <button onClick={saveRetention} className="col-span-2 bg-slate-900 text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700" data-testid="retention-save-btn">Save policy</button>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center gap-3 bg-gray-50">
          <button onClick={runArchive} disabled={archiving} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-white hover:border-slate-900 inline-flex items-center gap-2 disabled:opacity-60" data-testid="run-archive-btn">
            <Archive size={14} weight="bold" /> {archiving ? "Archiving…" : "Run archive now"}
          </button>
          <div className="text-xs text-gray-500 ml-2">Monthly cron runs automatically on the 1st at 02:00 CT.</div>
        </div>

        <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end border-b border-gray-200">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500 flex items-center gap-1"><CalendarBlank size={12} /> From</label>
            <input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm font-mono" data-testid="export-from-date" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500 flex items-center gap-1"><CalendarBlank size={12} /> To</label>
            <input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm font-mono" data-testid="export-to-date" />
          </div>
          <button onClick={exportCsv} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 inline-flex items-center gap-2 self-end" data-testid="export-csv-btn">
            <Export size={14} weight="bold" /> Export CSV
          </button>
        </div>

        {retention?.archives?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="archives-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
                  <th className="px-6 py-2 font-semibold">Period</th>
                  <th className="px-3 py-2 font-semibold">Entries</th>
                  <th className="px-3 py-2 font-semibold">Size</th>
                  <th className="px-3 py-2 font-semibold">Archived</th>
                  <th className="px-3 py-2 font-semibold">Trigger</th>
                  <th className="px-3 py-2 font-semibold text-right"></th>
                </tr>
              </thead>
              <tbody>
                {retention.archives.map((a) => (
                  <tr key={a.id} className="data-row border-b border-gray-100 font-mono">
                    <td className="px-6 py-2 text-slate-700">{a.period_start} → {a.period_end}</td>
                    <td className="px-3 py-2">{a.entry_count.toLocaleString()}</td>
                    <td className="px-3 py-2 text-gray-600">{(a.size_bytes / 1024).toFixed(1)} KB</td>
                    <td className="px-3 py-2 text-gray-500">{a.created_at.slice(0, 10)}</td>
                    <td className="px-3 py-2 text-gray-500">{a.triggered_by}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => downloadArchive(a.id, `${a.period_start}_${a.period_end}`)} className="text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold" data-testid={`dl-archive-${a.id}`}>
                        <DownloadSimple size={12} weight="bold" /> Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="overline text-gray-500">Last 200 events</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Audit log</h2>
          </div>
          <div className="flex gap-2">
            <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="border border-gray-300 bg-white px-3 py-2 text-xs" data-testid="audit-action-filter">
              <option value="">All actions</option>
              <option value="LOGIN_SUCCESS">LOGIN_SUCCESS</option>
              <option value="LOGIN_FAILED">LOGIN_FAILED</option>
              <option value="LOGIN_LOCKED">LOGIN_LOCKED</option>
              <option value="VIEW_PRACTITIONER">VIEW_PRACTITIONER</option>
              <option value="UPDATE_PRACTITIONER">UPDATE_PRACTITIONER</option>
              <option value="CREATE_PRACTITIONER">CREATE_PRACTITIONER</option>
              <option value="DELETE_PRACTITIONER">DELETE_PRACTITIONER</option>
              <option value="CREATE_CREDENTIAL">CREATE_CREDENTIAL</option>
              <option value="UPDATE_CREDENTIAL">UPDATE_CREDENTIAL</option>
              <option value="DELETE_CREDENTIAL">DELETE_CREDENTIAL</option>
              <option value="UPLOAD_DOCUMENT">UPLOAD_DOCUMENT</option>
              <option value="DOWNLOAD_DOCUMENT">DOWNLOAD_DOCUMENT</option>
              <option value="VIEW_AUDIT_LOG">VIEW_AUDIT_LOG</option>
            </select>
            <input placeholder="user email" value={filters.user_email} onChange={(e) => setFilters({ ...filters, user_email: e.target.value })} className="border border-gray-300 bg-white px-3 py-2 text-xs" data-testid="audit-email-filter" />
            <button onClick={reloadLog} className="border border-gray-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100" data-testid="audit-apply-filters">Apply</button>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="audit-table">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
                <th className="px-6 py-2 font-semibold">Timestamp (UTC)</th>
                <th className="px-3 py-2 font-semibold">User</th>
                <th className="px-3 py-2 font-semibold">Role</th>
                <th className="px-3 py-2 font-semibold">Action</th>
                <th className="px-3 py-2 font-semibold">Resource</th>
                <th className="px-3 py-2 font-semibold">IP</th>
                <th className="px-3 py-2 font-semibold">OK</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.length === 0 && (
                <tr><td colSpan="7" className="p-10 text-center text-gray-500">No audit events match.</td></tr>
              )}
              {auditLog.map((r) => (
                <tr key={r.id} className="data-row border-b border-gray-100 font-mono">
                  <td className="px-6 py-2 text-slate-700">{r.timestamp.replace("T", " ").slice(0, 19)}</td>
                  <td className="px-3 py-2">{r.user_email || "—"}</td>
                  <td className="px-3 py-2 text-gray-600">{r.user_role || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 ${r.action.includes("FAILED") || r.action.includes("DELETE") || r.action.includes("LOCKED") ? "bg-red-100 text-red-800" : r.action.includes("VIEW") || r.action.includes("DOWNLOAD") ? "bg-sky-100 text-sky-800" : "bg-emerald-100 text-emerald-800"}`}>
                      {r.action}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{r.resource_type}{r.resource_id ? ` · ${r.resource_id.slice(0, 8)}…` : ""}</td>
                  <td className="px-3 py-2 text-gray-500">{r.ip_address || "—"}</td>
                  <td className="px-3 py-2">{r.success ? "✓" : "✗"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {showInvite && (
        <InviteUserModal
          onClose={() => setShowInvite(false)}
          onCreated={() => { setShowInvite(false); loadAll(); }}
        />
      )}

      {resetResult && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="reset-pw-result-modal">
          <div className="bg-white border border-gray-200 w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="overline text-gray-500">Password reset · one-time view</div>
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mt-0.5">New temp password</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className={`px-3 py-2 text-xs ${resetResult.sms_status?.status === "sent" ? "bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500" : "bg-amber-50 text-amber-900 border-l-2 border-amber-400"}`}>
                {resetResult.sms_status?.status === "sent"
                  ? <>✓ Texted to <strong>{resetResult.phone}</strong> via Twilio.</>
                  : <>SMS not sent: <span className="font-mono">{resetResult.sms_status?.error || resetResult.sms_status?.reason}</span>. Share manually below.</>}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">User</div>
                <code className="block bg-gray-50 border border-gray-200 px-3 py-2 text-xs font-mono mt-1">{resetResult.email}</code>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Temporary password</div>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 bg-gray-50 border border-gray-200 px-3 py-2 text-xs font-mono" data-testid="reset-pw-value">{resetResult.temp_password}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(resetResult.temp_password); toast.success("Copied"); }}
                    className="border border-gray-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100"
                  >Copy</button>
                </div>
                <div className="text-[11px] text-gray-500 mt-1">
                  Shown once. We don't store retrievable passwords (HIPAA §164.312(a)(2)(iv)). The user must change it after sign-in.
                </div>
              </div>
              <div className="flex justify-end pt-2 border-t border-gray-200">
                <button onClick={() => setResetResult(null)} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700" data-testid="reset-pw-done">Done</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InviteUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("admin");
  const [password, setPassword] = useState(() => generateTempPassword());
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", { email, name, role, password });
      toast.success(`${email} created as ${role}`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="invite-user-modal">
      <div className="bg-white border border-gray-200 w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="overline text-gray-500">§164.308(a)(3)(ii)(B) · Workforce clearance</div>
            <h3 className="font-heading text-2xl font-semibold text-slate-900 mt-0.5">Invite new user</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-slate-900 text-xl leading-none" data-testid="invite-modal-close">×</button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Email *</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" data-testid="invite-email" autoFocus />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Full name *</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" data-testid="invite-name" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Role *</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="invite-role">
              <option value="admin">admin — full system control + user management</option>
              <option value="coordinator">coordinator — manage practitioners &amp; credentials</option>
              <option value="auditor">auditor — read-only audit access</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Temporary password *</label>
            <div className="flex gap-2 mt-1">
              <input required value={password} onChange={(e) => setPassword(e.target.value)} className="flex-1 border border-gray-300 bg-white px-3 py-2 text-sm font-mono focus:border-slate-900 focus:outline-none" data-testid="invite-password" />
              <button type="button" onClick={() => setPassword(generateTempPassword())} className="border border-gray-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100" data-testid="invite-regen">Regenerate</button>
            </div>
            <div className="text-[11px] text-gray-500 mt-1">Min 12 chars · ≥1 letter · ≥1 digit. Share securely; the user can change it after first login.</div>
          </div>
          <div className="border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-900">
            <strong className="font-semibold">Audit:</strong> This action is logged as <span className="font-mono">ADMIN_CREATE_USER</span> with your identity, the new user's email, and the assigned role.
          </div>
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
            <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100">Cancel</button>
            <button type="submit" disabled={saving} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 disabled:opacity-60" data-testid="invite-submit">
              {saving ? "Creating…" : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function generateTempPassword() {
  // 16 chars, mix of letters + digits, satisfies validate_password (≥12, letter, digit)
  const letters = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz";
  const digits = "23456789";
  const all = letters + digits + "!@#$%&*";
  let out = "";
  // ensure at least 1 letter + 1 digit
  out += letters[Math.floor(Math.random() * letters.length)];
  out += digits[Math.floor(Math.random() * digits.length)];
  for (let i = 0; i < 14; i++) out += all[Math.floor(Math.random() * all.length)];
  return out.split("").sort(() => Math.random() - 0.5).join("");
}

function prettyKey(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function baaKey(vendor) {
  return vendor.toLowerCase().split(/[\s/(]/)[0];
}

function Kpi({ label, value, sub, color, icon: Icon, testid }) {
  return (
    <div className="p-5 border-r border-b md:border-b-0 border-gray-200 last:border-r-0" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="overline text-gray-500">{label}</div>
        {Icon && <Icon size={16} className="text-gray-400" />}
      </div>
      <div className="mt-2 font-heading text-3xl font-bold tracking-tight" style={{ color: color || "#0F172A" }}>{value}</div>
      <div className="mt-0.5 text-[11px] text-gray-500 uppercase tracking-wide">{sub}</div>
    </div>
  );
}
